import { useEffect, useMemo, useState } from "react";
import {
  groupRecordedEpisodes,
  isFailureEpisode,
  isSuccessEpisode,
  type RecordedEpisode,
  type RecordedVideo,
} from "./domain/recordedEpisode";

export type RecordedEpisodeFilter = "all" | "failures" | "successes";

export type RecordedEpisodeCounts = Record<RecordedEpisodeFilter, number>;

export type RecordedEpisodeGallery = {
  activeFilter: RecordedEpisodeFilter;
  counts: RecordedEpisodeCounts;
  episode: RecordedEpisode | null;
  episodeCount: number;
  episodes: readonly RecordedEpisode[];
  goToNextEpisode: () => void;
  goToPreviousEpisode: () => void;
  page: number;
  selectFilter: (filter: RecordedEpisodeFilter) => void;
};

/** 실패 영상 우선 필터와 에피소드 단위 이동 상태를 소유한다. */
export function useRecordedEpisodeGallery(
  videos: readonly RecordedVideo[],
): RecordedEpisodeGallery {
  const episodes = useMemo(() => groupRecordedEpisodes(videos), [videos]);
  const counts = useMemo<RecordedEpisodeCounts>(
    () => ({
      all: episodes.length,
      failures: episodes.filter(isFailureEpisode).length,
      successes: episodes.filter(isSuccessEpisode).length,
    }),
    [episodes],
  );
  // 실패 영상이 없을 때만 전체로 폴백한다. 요청 필터는 보존한다. 그래서 이후 실패가 생기면
  // 실패 필터로 자동으로 돌아간다.
  const [filter, selectFilter] = useState<RecordedEpisodeFilter>("failures");
  const activeFilter: RecordedEpisodeFilter =
    filter === "failures" && counts.failures === 0 && counts.all > 0 ? "all" : filter;
  const filteredEpisodes = useMemo(() => {
    if (activeFilter === "failures") return episodes.filter(isFailureEpisode);
    if (activeFilter === "successes") return episodes.filter(isSuccessEpisode);
    return episodes;
  }, [activeFilter, episodes]);
  const [page, setPage] = useState(0);

  useEffect(() => setPage(0), [activeFilter]);

  const safePage = Math.min(page, Math.max(0, filteredEpisodes.length - 1));

  return {
    activeFilter,
    counts,
    episode: filteredEpisodes[safePage] ?? null,
    episodeCount: filteredEpisodes.length,
    episodes,
    goToNextEpisode: () => setPage((value) => Math.min(filteredEpisodes.length - 1, value + 1)),
    goToPreviousEpisode: () => setPage((value) => Math.max(0, value - 1)),
    page: safePage,
    selectFilter,
  };
}
